"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import useSolopreneurProfile, { magasinDossier, useHydrate, useIdentite } from "@/hooks/useSolopreneurProfile";
import {
    TRADUCTEURS,
    TRADUCTEURS_IDENTITE,
    differences,
    dossierDepuisOutil,
    dossierVide,
    identiteDepuisOutil,
    identiteVide,
    champsLusPar,
    nettoyerDossier,
    valeursIdentitePourOutil,
    valeursPourOutil,
} from "@/lib/tools/dossier";
import { dateLongue, euros, libelleChamp, valeurLisible } from "@/lib/tools/dossierTextes";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import ToolsDossierQuestions from "./ToolsDossierQuestions";
import useDossierCompte from "@/hooks/useDossierCompte";
import ToolsIdentitySheet from "./ToolsIdentity";
import { ContexteReprise, HauteurFluide, lireReprise, oublierReprise, useResultatAffiche } from "./Journey";

/**
 * Le cadre d'un outil : il le remplit avec le dossier du lecteur, et propose
 * de garder dans le dossier ce que le lecteur y change.
 *
 * L'OUTIL NE CONNAÎT PAS LE DOSSIER, SEULEMENT SES PROPRES NOMS. Il demande
 * ses valeurs de départ par useDossierOutil(), déjà traduites dans son
 * vocabulaire (lib/tools/dossier.js), et annonce ses réponses du moment par
 * useSignalerDossier(). Tout le reste, la traduction, la comparaison et la
 * proposition, se passe ici.
 *
 * LE DOSSIER S'APPLIQUE APRÈS L'HYDRATATION, EN REMONTANT L'OUTIL. Le serveur
 * ne connaît pas le stockage du navigateur : il rend l'outil vierge, et le
 * premier rendu du navigateur doit rendre la même chose. Une fois hydraté,
 * s'il y a quelque chose à reprendre, l'outil est remonté une seule fois avec
 * ses valeurs de départ, ce que ses useState lisent comme n'importe quelle
 * valeur initiale. Celui qui n'a pas de dossier ne voit aucun remontage.
 *
 * LES VALEURS DE DÉPART SONT PRISES UNE FOIS. Accepter « Oui, partout » change
 * le dossier, pas l'outil qu'on est en train de lire : le remonter effacerait
 * ce que le lecteur vient de faire.
 *
 * ON NE PROPOSE QUE CE QUE LE LECTEUR A CHANGÉ. Les premières réponses
 * annoncées par l'outil servent de référence : une valeur par défaut que
 * personne n'a touchée n'a rien à faire dans le dossier.
 */

// Exporté pour scripts/check-dossier-outils.mjs, qui rend chaque outil avec un
// dossier sans passer par l'hydratation.
export const ContexteDossier = createContext({
    valeurs: {},
    identite: {},
    signaler: () => {},
    signalerIdentite: () => {},
});

/** Les valeurs de départ de l'outil, sous ses propres noms. Vides hors du cadre. */
export function useDossierOutil() {
    return useContext(ContexteDossier);
}

/**
 * Annonce les réponses du moment. L'objet peut être neuf à chaque rendu : on
 * ne réagit qu'à un contenu différent.
 *
 * `pret` dit que le lecteur voit son résultat (ou son document). La
 * proposition de garder ses réponses attend ce moment : l'afficher après la
 * première question couperait le parcours.
 */
export function useSignalerDossier(valeurs, pret = true) {
    const { signaler } = useContext(ContexteDossier);
    const texte = JSON.stringify(valeurs ?? {});
    useEffect(() => {
        signaler(JSON.parse(texte), Boolean(pret));
    }, [texte, pret, signaler]);
}

export function useSignalerIdentite(valeurs, pret = true) {
    const { signalerIdentite } = useContext(ContexteDossier);
    const texte = JSON.stringify(valeurs ?? {});
    useEffect(() => {
        signalerIdentite(JSON.parse(texte), Boolean(pret));
    }, [texte, pret, signalerIdentite]);
}

const T = {
    fr: {
        rempli: "Rempli avec votre dossier.",
        rempliAvec: "Rempli avec votre dossier :",
        modifier: "Modifier",
        feuilleTitre: "Ce que cet outil a repris de votre dossier",
        feuilleIntro: "Corrigez une réponse : l'outil repart aussitôt avec la nouvelle valeur, et votre dossier la garde.",
        resume: {
            debut: "depuis le {date}",
            debutProjet: "démarrage le {date}",
            ca: "{montant} de chiffre d'affaires",
            seul: "seul",
            couple: "en couple",
            sansEnfant: "sans enfant",
            unEnfant: "un enfant",
            enfants: "{n} enfants",
            objectif: "{montant} visés par mois",
        },
        questions: { statut: "Statut", activite: "Activité", debut: "Début d'activité", ca: "Chiffre d'affaires", foyer: "Foyer", objectif: "Objectif" },
        rempliDetail: "Modifier une valeur ici ne change pas votre dossier, sauf si vous le demandez.",
        voirSituation: "Voir ma situation",
        zero: "Repartir de zéro",
        repriseTitre: "Vous aviez commencé.",
        repriseDetail: "Vos réponses sont restées dans cet onglet.",
        reprendre: "Reprendre où j'en étais",
        suite: "Et maintenant ?",
        partager: "Copier le lien de ce résultat",
        partageCopie: "Lien copié",
        partageAide: "Le lien contient vos réponses (chiffre d'affaires, foyer…) : ne l'envoyez qu'à qui vous voulez.",
        partageTitre: "On vous a partagé ce calcul.",
        partageSous: "L'outil reprend les réponses de la personne qui vous a envoyé le lien. Changez-les à votre guise : rien n'est gardé sans votre accord.",
        partageGardeCourt: "Gardé dans votre dossier",
        partageGarder: "Le garder dans mon dossier",
        partageGarde: "C'est gardé dans votre dossier.",
        inviter: "Répondez une fois à six questions : cet outil et les autres se rempliront seuls.",
        creer: "Créer mon dossier",
        garderTitre: "Garder ces réponses dans votre dossier ?",
        creerTitre: "Gardez ces réponses pour les autres outils ?",
        creerDetail: "Elles restent sur cet appareil. Les autres outils se rempliront seuls.",
        creerDetailCompte: "Elles suivent votre compte. Les autres outils se rempliront seuls, sur tous vos appareils.",
        oui: "Oui, partout",
        seulementIci: "Seulement ici",
        garde: "C'est gardé dans votre dossier.",
        identiteRemplie: "Rempli avec votre identité, gardée sur cet appareil.",
        identiteModifier: "Modifier mon identité",
        identiteInviter: "Enregistrez votre identité une fois : elle remplira les douze formulaires. Elle reste sur cet appareil.",
        identiteEnregistrer: "Enregistrer mon identité",
        identiteGarderTitre: "Garder votre identité pour les autres formulaires ?",
        identiteGarderDetail: "Elle reste sur cet appareil, et n'est jamais envoyée.",
        identiteGardee: "Votre identité est gardée sur cet appareil.",
        fleche: "→",
    },
    en: {
        rempli: "Filled in from your profile.",
        rempliAvec: "Filled in from your profile:",
        modifier: "Edit",
        feuilleTitre: "What this tool took from your profile",
        feuilleIntro: "Correct an answer: the tool restarts at once with the new value, and your profile keeps it.",
        resume: {
            debut: "since {date}",
            debutProjet: "starting on {date}",
            ca: "{montant} of revenue",
            seul: "single",
            couple: "as a couple",
            sansEnfant: "no children",
            unEnfant: "one child",
            enfants: "{n} children",
            objectif: "{montant} targeted a month",
        },
        questions: { statut: "Status", activite: "Activity", debut: "Start of activity", ca: "Revenue", foyer: "Household", objectif: "Goal" },
        rempliDetail: "Changing a value here does not change your profile, unless you ask.",
        voirSituation: "See my situation",
        zero: "Start from scratch",
        repriseTitre: "You had started.",
        repriseDetail: "Your answers stayed in this tab.",
        reprendre: "Pick up where I left off",
        suite: "What's next?",
        partager: "Copy the link to this result",
        partageCopie: "Link copied",
        partageAide: "The link contains your answers (revenue, household…): only send it to people you choose.",
        partageTitre: "Someone shared this calculation with you.",
        partageSous: "The tool uses the answers of the person who sent you the link. Change them as you like: nothing is kept without your consent.",
        partageGardeCourt: "Saved in your profile",
        partageGarder: "Keep it in my profile",
        partageGarde: "Saved in your profile.",
        inviter: "Answer six questions once: this tool and the others will fill themselves in.",
        creer: "Create my profile",
        garderTitre: "Keep these answers in your profile?",
        creerTitre: "Keep these answers for the other tools?",
        creerDetail: "They stay on this device. The other tools will fill themselves in.",
        creerDetailCompte: "They follow your account. The other tools will fill themselves in, on all your devices.",
        oui: "Yes, everywhere",
        seulementIci: "Only here",
        garde: "Saved in your profile.",
        identiteRemplie: "Filled in with your identity, kept on this device.",
        identiteModifier: "Edit my identity",
        identiteInviter: "Save your identity once: it will fill in the twelve forms. It stays on this device.",
        identiteEnregistrer: "Save my identity",
        identiteGarderTitre: "Keep your identity for the other forms?",
        identiteGarderDetail: "It stays on this device and is never sent.",
        identiteGardee: "Your identity is saved on this device.",
        fleche: "→",
    },
};

/** Les liens seuls d'un bandeau : une cible de 44 pixels de haut, même quand le texte est court. */
const LIEN_SEUL = "inline-flex min-h-[44px] items-center font-semibold text-gray-900 dark:text-white underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary transition-colors duration-200";
// Les actions du bandeau, sous ses valeurs : plus petites que le texte
// qu'elles servent, mais une cible de 36 pixels sous le doigt.
const LIEN_PETIT = "inline-flex min-h-[36px] items-center font-semibold text-gray-700 dark:text-lightBlue underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary hover:text-gray-900 dark:hover:text-white transition-colors duration-200";
const LIEN = "font-semibold text-gray-900 dark:text-white underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary transition-colors duration-200";
const BOUTON_PLEIN =
    "min-h-[44px] rounded-lg bg-gray-900 dark:bg-white px-4 py-2.5 text-sm font-semibold text-white dark:text-gray-900 transition-opacity hover:opacity-90";
const BOUTON_VIDE =
    "min-h-[44px] rounded-lg border border-gray-200 dark:border-white/15 bg-white dark:bg-transparent px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-lightBlue";

/** Les changements proposés, un par ligne : « Chiffre d'affaires : 48 000 € → 55 000 € ». */
function ListeChangements({ changements, lang }) {
    return (
        <ul className="mt-2 mb-0 space-y-1 text-sm text-gray-600 dark:text-lightBlue">
            {changements.map(({ champ, avant, apres }) => (
                <li key={champ}>
                    <span className="text-gray-900 dark:text-white">{libelleChamp(champ, lang)}</span>
                    {" : "}
                    {avant !== null && avant !== undefined && avant !== "" && (
                        <>
                            <span className="line-through decoration-gray-400">{valeurLisible(champ, avant, lang)}</span>{" "}
                            {T[lang]?.fleche || "→"}{" "}
                        </>
                    )}
                    <span className="font-semibold text-gray-900 dark:text-white">{valeurLisible(champ, apres, lang)}</span>
                </li>
            ))}
        </ul>
    );
}

function Proposition({ titre, detail, changements, lang, onOui, onNon }) {
    const t = T[lang] || T.fr;
    const reduit = useReducedMotion();
    return (
        <motion.section
            initial={reduit ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduit ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: 6 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            aria-label={titre}
            className="mt-6 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white/80 dark:bg-white/[0.03] px-5 py-4"
        >
            <div className="flex items-start gap-3">
                <SolarIcon name="folder-with-files-linear" className="mt-0.5 h-5 w-5 shrink-0 text-gray-400 dark:text-lightBlue/60" />
                <div className="min-w-0 flex-1">
                    <p className="mb-0 font-semibold dark:text-white">{titre}</p>
                    {detail && <p className="mt-0.5 mb-0 text-sm text-gray-600 dark:text-lightBlue">{detail}</p>}
                    <ListeChangements changements={changements} lang={lang} />
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" onClick={onOui} className={BOUTON_PLEIN}>
                            {t.oui}
                        </button>
                        <button type="button" onClick={onNon} className={BOUTON_VIDE}>
                            {t.seulementIci}
                        </button>
                    </div>
                </div>
            </div>
        </motion.section>
    );
}

function Confirmation({ texte }) {
    return (
        <motion.p
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-6 mb-0 flex items-center gap-2 text-sm text-gray-600 dark:text-lightBlue"
        >
            <SolarIcon name="check-circle-linear" className="h-4 w-4 shrink-0 text-primary" />
            {texte}
        </motion.p>
    );
}

/** Les réponses d'un calcul partagé qu'on peut garder : celles des six questions. */
const CHAMPS_GARDES = [
    "statut",
    "activite",
    "artisan",
    "dateDebut",
    "caCetteAnnee",
    "caAnDernier",
    "enCouple",
    "enfants",
    "autresRevenus",
    "objectifMensuel",
    "fraisMensuels",
    "optionFiscale",
];

/** La question du dossier qui répond à chaque champ. */
const QUESTION_DU_CHAMP = {
    statut: "statut",
    activite: "activite",
    artisan: "activite",
    dateDebut: "debut",
    caCetteAnnee: "ca",
    caAnDernier: "ca",
    enCouple: "foyer",
    enfants: "foyer",
    autresRevenus: "foyer",
    objectifMensuel: "objectif",
    fraisMensuels: "objectif",
};

/** Ce que dit une question du dossier, en quelques mots : « 48 000 € de chiffre d'affaires ». */
function resumeQuestion(q, d, t, lang) {
    const r = t.resume;
    const remplir = (x, v) => x.replace(/\{(\w+)\}/g, (_, k) => v[k]);
    if (q === "statut") return d.statut ? valeurLisible("statut", d.statut, lang) : null;
    if (q === "activite") return d.activite ? valeurLisible("activite", d.activite, lang) : null;
    if (q === "debut") return d.dateDebut ? remplir(d.statut === "projet" ? r.debutProjet : r.debut, { date: dateLongue(d.dateDebut, lang) }) : null;
    if (q === "ca") return d.caCetteAnnee !== null ? remplir(r.ca, { montant: euros(d.caCetteAnnee, lang) }) : null;
    if (q === "foyer") {
        if (d.enCouple === null) return null;
        const enfants = d.enfants === null ? null : d.enfants === 0 ? r.sansEnfant : d.enfants === 1 ? r.unEnfant : remplir(r.enfants, { n: d.enfants });
        return [d.enCouple ? r.couple : r.seul, enfants].filter(Boolean).join(", ");
    }
    if (q === "objectif") return d.objectifMensuel !== null ? remplir(r.objectif, { montant: euros(d.objectifMensuel, lang) }) : null;
    return null;
}

/**
 * LE LIEN D'UN CALCUL. Les réponses de l'outil, dans la langue du dossier,
 * vont dans l'adresse (?reponses=…), encodées en base64 d'URL. Celui qui
 * ouvre le lien voit l'outil rempli avec elles ; rien n'est écrit dans son
 * dossier tant qu'il ne le demande pas. Seules les réponses que le dossier
 * connaît voyagent : les questions propres à l'outil se reposent.
 */
function encoderReponses(partiel) {
    const texte = JSON.stringify(partiel);
    const base64 = btoa(unescape(encodeURIComponent(texte)));
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function lireReponsesPartagees() {
    try {
        const brut = new URLSearchParams(window.location.search).get("reponses");
        if (!brut || brut.length > 2000) return null;
        const base64 = brut.replace(/-/g, "+").replace(/_/g, "/");
        const texte = decodeURIComponent(escape(atob(base64)));
        const d = nettoyerDossier(JSON.parse(texte));
        return dossierVide(d) ? null : d;
    } catch (e) {
        return null;
    }
}

/** Aucune annonce encore : la forme de l'état des annonces. */
const VIDE = { cle: null, base: null, actuel: null, pret: false, baseId: null, actuelId: null, pretId: false };

/**
 * Ce qu'on propose de garder, une fois le résultat affiché.
 *
 * RIEN TANT QUE LE LECTEUR N'A RIEN TOUCHÉ. Un outil qu'on ouvre et qu'on
 * lit sans rien changer n'a rien appris au dossier : il faut au moins une
 * valeur différente de la référence (les valeurs de départ).
 *
 * UN CHOIX COMPTE MÊME S'IL TOMBE SUR LA VALEUR PAR DÉFAUT. Celui qui répond
 * « seul » à la question du foyer a répondu, même si l'outil partait sur
 * « seul » : arrivé au résultat, il est passé par cette question. Un montant,
 * lui, ne compte que s'il a bougé : le curseur posé à 40 000 € par l'outil
 * n'est pas le chiffre d'affaires du lecteur.
 */
const CHOIX = new Set(["statut", "activite", "artisan", "dateDebut", "enCouple", "enfants", "optionFiscale", "civilite"]);

function aProposer(base, actuel, dossier) {
    if (!actuel || !base) return [];
    const touche = Object.keys(actuel).some((c) => (base[c] ?? null) !== actuel[c]);
    if (!touche) return [];
    return differences(dossier, actuel).filter(
        ({ champ }) => CHOIX.has(champ) || (base[champ] ?? null) !== actuel[champ],
    );
}

/** Les actions d'un résultat : la même pastille que les boutons du dossier en haut de /outils. */
export const PASTILLE_ACTION =
    "inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] px-3.5 text-sm font-semibold text-gray-800 dark:text-white transition-colors duration-200 hover:border-gray-300 dark:hover:border-white/25 disabled:opacity-60";

export default function ToolsDossier({ composant, lang = "fr", aujourdhui, actions = null, suite = null, children }) {
    const t = T[lang] || T.fr;
    const hydrate = useHydrate();
    const { profil, enregistrer } = useSolopreneurProfile();
    const { identite, enregistrer: enregistrerIdentite } = useIdentite();

    const estSimulateur = Boolean(TRADUCTEURS[composant]);
    const estFormulaire = Boolean(TRADUCTEURS_IDENTITE[composant]);
    // Connecté, le dossier suit le compte ; sans compte, rien ne part. Un
    // outil que le dossier ne remplit pas n'interroge pas le compte.
    const { connecte } = useDossierCompte(lang, { actif: estSimulateur });

    // Chaque « Repartir de zéro » ou chaque identité enregistrée ouvre une
    // nouvelle séance : l'outil est remonté, et la référence repart à neuf.
    const [seance, setSeance] = useState({ n: 0, vierge: false });
    const cle = hydrate ? `seance-${seance.n}` : "serveur";

    // Les valeurs de départ, prises une fois par séance (voir plus haut).
    const [depart, setDepart] = useState({ cle: "serveur", valeurs: {}, identite: {}, partage: null });
    if (depart.cle !== cle) {
        // Un calcul partagé par lien passe avant le dossier du lecteur, à la
        // première séance seulement : « Repartir de zéro » ou une correction
        // reviennent au dossier.
        const partage = hydrate && estSimulateur && seance.n === 0 ? lireReponsesPartagees() : null;
        setDepart({
            cle,
            valeurs: hydrate && !seance.vierge ? valeursPourOutil(composant, partage || profil, aujourdhui) : {},
            identite: hydrate && !seance.vierge ? valeursIdentitePourOutil(composant, identite) : {},
            partage,
        });
    }
    const rempli = Object.keys(depart.valeurs).length > 0;
    const identiteRemplie = Object.keys(depart.identite).length > 0;

    // Référence et réponses du moment, par séance.
    const [annonces, setAnnonces] = useState(VIDE);
    const signaler = useCallback(
        (valeurs, pret) => {
            setAnnonces((a) => {
                const partiel = dossierDepuisOutil(composant, valeurs, magasinDossier.lire());
                const courant = a.cle === cle ? a : { ...VIDE, cle };
                return { ...courant, base: courant.base ?? partiel, actuel: partiel, pret };
            });
        },
        [composant, cle],
    );
    const signalerIdentite = useCallback(
        (valeurs, pret) => {
            setAnnonces((a) => {
                const partiel = identiteDepuisOutil(composant, valeurs);
                const courant = a.cle === cle ? a : { ...VIDE, cle };
                return { ...courant, baseId: courant.baseId ?? partiel, actuelId: partiel, pretId: pret };
            });
        },
        [composant, cle],
    );

    const [confirme, setConfirme] = useState(null);
    // Le bouton de partage dit lui-même que le lien est copié, deux secondes.
    const [lienCopie, setLienCopie] = useState(false);
    useEffect(() => {
        if (!lienCopie) return undefined;
        const minuteur = setTimeout(() => setLienCopie(false), 2200);
        return () => clearTimeout(minuteur);
    }, [lienCopie]);
    const [partageGarde, setPartageGarde] = useState(false);
    // Ce que le lecteur d'écran entend quand un bouton change d'état.
    const [annonce, setAnnonce] = useState("");
    useEffect(() => {
        if (!confirme) return undefined;
        const minuteur = setTimeout(() => setConfirme(null), 4000);
        return () => clearTimeout(minuteur);
    }, [confirme]);

    const [feuille, setFeuille] = useState(false);
    // La feuille « Ce que cet outil a repris » : la liste, puis une question.
    const [reprise, setReprise] = useState({ ouverte: false, question: null });

    const aJour = annonces.cle === cle;
    const changements = useMemo(
        () => (hydrate && aJour && estSimulateur && annonces.pret ? aProposer(annonces.base, annonces.actuel, profil) : []),
        [hydrate, aJour, estSimulateur, annonces.pret, annonces.base, annonces.actuel, profil],
    );
    const changementsIdentite = useMemo(
        () => (hydrate && aJour && estFormulaire && annonces.pretId ? aProposer(annonces.baseId, annonces.actuelId, identite) : []),
        [hydrate, aJour, estFormulaire, annonces.pretId, annonces.baseId, annonces.actuelId, identite],
    );

    const garder = () => {
        enregistrer(Object.fromEntries(changements.map((c) => [c.champ, c.apres])));
        setAnnonces((a) => ({ ...a, base: a.actuel }));
        setConfirme(t.garde);
    };
    const garderIdentite = () => {
        enregistrerIdentite(Object.fromEntries(changementsIdentite.map((c) => [c.champ, c.apres])));
        setAnnonces((a) => ({ ...a, baseId: a.actuelId }));
        setConfirme(t.identiteGardee);
    };

    const resultatAffiche = useResultatAffiche();
    const reduitMouvement = useReducedMotion();
    // La reprise se lit une fois, après l'hydratation : ensuite l'outil
    // l'écrit à chaque pas, et le bandeau ne doit pas apparaître en cours de
    // route.
    const [repriseDispo, setRepriseDispo] = useState(undefined);
    if (hydrate && repriseDispo === undefined) setRepriseDispo(lireReprise(composant));
    const [demande, setDemande] = useState(null);
    const valeurReprise = useMemo(
        () => ({ cle: composant, demande: demande && demande.seance === seance.n ? demande : null }),
        [composant, demande, seance.n],
    );
    const reprendre = () => {
        setDemande({ ...repriseDispo, n: Date.now(), seance: seance.n });
        setRepriseDispo(null);
    };
    const oublierLaReprise = () => {
        oublierReprise(composant);
        setRepriseDispo(null);
    };

    const contexte = useMemo(
        () => ({ valeurs: depart.valeurs, identite: depart.identite, signaler, signalerIdentite }),
        [depart.valeurs, depart.identite, signaler, signalerIdentite],
    );

    // ------------------------------------------------------------------
    // Pour tous les outils, reliés au dossier ou non : la reprise, la ligne
    // des actions du résultat et « Et maintenant ? ».
    // ------------------------------------------------------------------
    // Partager ce calcul, une fois le résultat affiché.
    const partageVisible = hydrate && estSimulateur && aJour && annonces.pret && annonces.actuel && Object.keys(annonces.actuel).length > 0;

    const ligneActions = (
        /* LES ACTIONS DU RÉSULTAT, SUR UNE LIGNE : partager ce calcul et
           télécharger sa note (passée par la page, `actions`). La ligne
           disparaît tant qu'aucune n'a de bouton à montrer. */
        <div className="mt-5 hidden flex-col gap-1.5 has-[button]:flex">
            <div className="flex flex-wrap items-center gap-2">
                {partageVisible && (
                    <button
                        type="button"
                        onClick={async () => {
                            const lien = `${window.location.origin}${window.location.pathname}?reponses=${encoderReponses(annonces.actuel)}`;
                            try {
                                await navigator.clipboard.writeText(lien);
                                setLienCopie(true);
                                setAnnonce(t.partageCopie);
                            } catch (e) {
                                window.prompt(t.partager, lien);
                            }
                        }}
                        // LE BOUTON RÉPOND LUI-MÊME : coche verte et « Lien copié »
                        // pendant deux secondes, là où le regard est déjà. La
                        // confirmation s'affichait trois blocs plus bas.
                        className={`${PASTILLE_ACTION} ${lienCopie ? "!border-green-600/40 !bg-green-50 !text-green-800 dark:!bg-green-500/10 dark:!text-green-300" : ""}`}
                    >
                        {/* Un fondu d'entrée seulement : attendre la sortie de
                            l'ancien libellé retardait « Lien copié ». */}
                        <motion.span
                                key={lienCopie ? "copie" : "copier"}
                                className="inline-flex items-center gap-1.5"
                                {...(reduitMouvement ? {} : { initial: { opacity: 0, y: 3 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.15 } })}
                            >
                                <SolarIcon
                                    name={lienCopie ? "check-circle-bold" : "link-round-linear"}
                                    className={`h-4 w-4 ${lienCopie ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-lightBlue/70"}`}
                                />
                                {lienCopie ? t.partageCopie : t.partager}
                            </motion.span>
                    </button>
                )}
                {actions}
            </div>
            {partageVisible && <span className="text-xs text-gray-500 dark:text-lightBlue/70">{t.partageAide}</span>}
        </div>
    );

    // ET MAINTENANT ? Le moment où l'on est prêt pour l'outil suivant, c'est
    // juste après le résultat, pas quinze écrans plus bas. La page passe le
    // premier outil voisin du registre (`suite`).
    const carteSuite =
        suite && resultatAffiche ? (
            <motion.div
                className="mt-4"
                {...(reduitMouvement ? {} : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: 0.15 } })}
            >
                <Link
                    href={{ pathname: "/tools/[slug]", params: { slug: suite.slug } }}
                    className="group flex items-center gap-4 rounded-2xl border border-primary/25 bg-primary/[0.04] dark:bg-primary/[0.08] px-4 py-3.5 sm:px-5 transition-colors duration-200 hover:border-primary/50 hover:bg-primary/[0.07]"
                >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                        <SolarIcon name={suite.icone || "arrow-right-linear"} className="h-5 w-5 text-primary" />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold uppercase tracking-wider text-[#B83C12] dark:text-[#FF8A5B]">{t.suite}</span>
                        <span className="block font-semibold text-gray-900 dark:text-white transition-colors group-hover:text-[#B83C12] dark:group-hover:text-[#FF8A5B]">{suite.nom}</span>
                        <span className="block text-sm text-gray-600 dark:text-lightBlue line-clamp-2 sm:line-clamp-1 group-hover:text-gray-600 dark:group-hover:text-lightBlue">
                            {suite.description}
                        </span>
                    </span>
                    <SolarIcon name="arrow-right-linear" className="h-5 w-5 shrink-0 text-[#B83C12] dark:text-[#FF8A5B] transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
            </motion.div>
        ) : null;

    // REPRENDRE OÙ L'ON EN ÉTAIT : une proposition, jamais une reprise
    // imposée. Le lecteur qui revient choisit de repartir de ses réponses ou
    // de zéro.
    const banniereReprise =
        hydrate && repriseDispo ? (
            <div className="mb-4 flex flex-col gap-2 rounded-2xl border border-primary/25 bg-[#fff8f4] dark:bg-[#D04515]/10 px-4 py-3 text-sm text-gray-600 dark:text-lightBlue sm:flex-row sm:items-center sm:justify-between">
                <p className="mb-0 flex items-start gap-2">
                    <SolarIcon name="history-linear" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                        <span className="font-semibold text-gray-900 dark:text-white">{t.repriseTitre}</span> {t.repriseDetail}
                    </span>
                </p>
                <span className="flex shrink-0 items-center gap-4 pl-6 sm:pl-0">
                    <button
                        type="button"
                        onClick={reprendre}
                        className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full bg-[#D04515] px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#B83C12]"
                    >
                        {t.reprendre}
                    </button>
                    <button type="button" onClick={oublierLaReprise} className={LIEN_SEUL}>
                        {t.zero}
                    </button>
                </span>
            </div>
        ) : null;

    if (!estSimulateur && !estFormulaire) {
        return (
            <ContexteReprise.Provider value={valeurReprise}>
                {banniereReprise}
                <HauteurFluide>{children}</HauteurFluide>
                {ligneActions}
                {carteSuite}
            </ContexteReprise.Provider>
        );
    }

    // Avant l'hydratation, le dossier est vide pour tout le monde : l'invitation
    // se rend donc dès le serveur, et rien ne s'insère au-dessus de l'outil
    // une fois la page chargée. Seul son texte peut changer ensuite.
    const dossierAbsent = !hydrate || dossierVide(profil);
    // Les questions du dossier que cet outil a reprises, pour le bandeau.
    const questionsReprises = rempli
        ? [...new Set(champsLusPar(composant, depart.partage || profil, aujourdhui).map((c) => QUESTION_DU_CHAMP[c]).filter(Boolean))]
        : [];
    const sansIdentite = !hydrate || identiteVide(identite);

    return (
        <ContexteReprise.Provider value={valeurReprise}>
        <ContexteDossier.Provider value={contexte}>
            {/* Le serveur ne sait rien du dossier : il rend l'invitation, que le
                premier rendu du navigateur rend à l'identique (le dossier y est
                encore vide). Une fois hydraté, un dossier qui remplit l'outil
                remplace l'invitation par le bandeau « Rempli », de même hauteur. */}
            {/* ARRIVÉE PAR UN LIEN PARTAGÉ : un accueil à part. Le lecteur ne
                sait pas encore ce qu'il regarde ; on le lui dit, on montre les
                réponses reçues, et l'action utile (les garder) passe devant.
                « Voir ma situation » n'a rien à faire ici : ce n'est pas la
                sienne. */}
            {estSimulateur && rempli && depart.partage && (
                <div className="mb-4 rounded-2xl border border-primary/25 bg-[#fff8f4] dark:bg-[#D04515]/10 px-4 py-4 sm:px-5">
                    <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                            <SolarIcon name="link-round-linear" className="h-5 w-5 text-primary" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="mb-0 font-semibold text-gray-900 dark:text-white">{t.partageTitre}</p>
                            <p className="mb-0 mt-0.5 text-sm text-gray-600 dark:text-lightBlue">{t.partageSous}</p>
                            {questionsReprises.length > 0 && (
                                <ul className="m-0 mt-3 flex list-none flex-wrap gap-1.5 p-0">
                                    {questionsReprises
                                        .map((q) => [q, resumeQuestion(q, depart.partage, t, lang)])
                                        .filter(([, texte]) => texte)
                                        .map(([q, texte]) => (
                                            <li
                                                key={q}
                                                className="rounded-full border border-gray-200/80 dark:border-white/10 bg-white dark:bg-white/[0.06] px-2.5 py-1 text-[13px] text-gray-700 dark:text-lightBlue"
                                            >
                                                {texte.charAt(0).toUpperCase() + texte.slice(1)}
                                            </li>
                                        ))}
                                </ul>
                            )}
                            <div className="mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                                <button
                                    type="button"
                                    disabled={partageGarde}
                                    onClick={() => {
                                        enregistrer(Object.fromEntries(Object.entries(depart.partage).filter(([c, v]) => CHAMPS_GARDES.includes(c) && v !== null)));
                                        setPartageGarde(true);
                                        setAnnonce(t.partageGarde);
                                    }}
                                    className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-4 text-sm font-semibold transition-colors duration-200 ${
                                        partageGarde
                                            ? "border border-green-600/40 bg-green-50 text-green-800 dark:bg-green-500/10 dark:text-green-300"
                                            : "bg-[#D04515] text-white hover:bg-[#B83C12]"
                                    }`}
                                >
                                    <SolarIcon name={partageGarde ? "check-circle-bold" : "folder-check-linear"} className="h-4 w-4" />
                                    {partageGarde ? t.partageGardeCourt : t.partageGarder}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        oublierReprise(composant);
                                        setSeance((x) => ({ n: x.n + 1, vierge: true }));
                                    }}
                                    className="text-sm font-semibold text-gray-700 dark:text-lightBlue underline decoration-gray-300 underline-offset-4 hover:decoration-primary"
                                >
                                    {t.zero}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Le serveur ne sait rien du dossier : il rend l'invitation, que le
                premier rendu du navigateur rend à l'identique (le dossier y est
                encore vide). Une fois hydraté, un dossier qui remplit l'outil
                remplace l'invitation par le bandeau « Rempli ». Les valeurs
                prennent toute la largeur, les actions passent dessous en petit :
                côte à côte, elles écrasaient le texte sur cinq lignes. */}
            {estSimulateur && ((rempli && !depart.partage) || dossierAbsent) && (
                <div className="mb-4 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] px-4 py-3 text-sm text-gray-600 dark:text-lightBlue">
                    {rempli ? (
                        <>
                            {/* AVEC QUOI L'OUTIL EST REMPLI, EN CLAIR. Chaque
                                valeur se corrige d'un clic : elle ouvre
                                directement sa question. */}
                            <p className="mb-0 flex items-start gap-2">
                                <SolarIcon name="folder-check-linear" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                <span>
                                    <span className="font-semibold text-gray-900 dark:text-white">
                                        {questionsReprises.length ? t.rempliAvec : t.rempli}
                                    </span>{" "}
                                    {questionsReprises.length === 0
                                        ? t.rempliDetail
                                        : questionsReprises
                                              .map((q) => [q, resumeQuestion(q, profil, t, lang)])
                                              .filter(([, texte]) => texte)
                                              .map(([q, texte], i) => (
                                                  <span key={q}>
                                                      {i > 0 && <span aria-hidden="true"> · </span>}
                                                      <button
                                                          type="button"
                                                          onClick={() => setReprise({ ouverte: true, question: q, directe: true })}
                                                          className="text-gray-700 dark:text-lightBlue underline decoration-dotted decoration-gray-400 dark:decoration-white/30 underline-offset-4 hover:decoration-primary hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                                                      >
                                                          <span className="sr-only">{t.modifier} : </span>
                                                          {texte}
                                                      </button>
                                                  </span>
                                              ))}
                                </span>
                            </p>
                            <span className="mt-1 flex flex-wrap gap-x-4 pl-6 text-[13px]">
                                {questionsReprises.length > 0 && (
                                    <button type="button" onClick={() => setReprise({ ouverte: true, question: null })} className={LIEN_PETIT}>
                                        {t.modifier}
                                    </button>
                                )}
                                <Link href="/tools/my-situation" className={LIEN_PETIT}>
                                    {t.voirSituation}
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => {
                                        oublierReprise(composant);
                                        setSeance((x) => ({ n: x.n + 1, vierge: true }));
                                    }}
                                    className={LIEN_PETIT}
                                >
                                    {t.zero}
                                </button>
                            </span>
                        </>
                    ) : (
                        <p className="mb-0 flex items-start gap-2">
                            <SolarIcon name="folder-with-files-linear" className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <span>
                                {t.inviter}{" "}
                                <Link href="/tools/my-situation" className={LIEN}>
                                    {t.creer}
                                </Link>
                            </span>
                        </p>
                    )}
                </div>
            )}

            {estFormulaire && (identiteRemplie || sansIdentite) && (
                <div className="mb-4 flex flex-col gap-1 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] px-4 pt-3 pb-1 sm:py-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 text-sm text-gray-600 dark:text-lightBlue">
                    {identiteRemplie ? (
                        <>
                            <p className="mb-0 flex items-start gap-2">
                                <SolarIcon name="user-check-linear" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                <span className="font-semibold text-gray-900 dark:text-white">{t.identiteRemplie}</span>
                            </p>
                            <span className="flex shrink-0 gap-4 pl-6 sm:pl-0">
                                <button type="button" onClick={() => setFeuille(true)} className={LIEN_SEUL}>
                                    {t.identiteModifier}
                                </button>
                                <button type="button" onClick={() => {
                                        oublierReprise(composant);
                                        setSeance((s) => ({ n: s.n + 1, vierge: true }));
                                    }} className={LIEN_SEUL}>
                                    {t.zero}
                                </button>
                            </span>
                        </>
                    ) : (
                        <p className="mb-0 flex items-start gap-2">
                            <SolarIcon name="user-id-linear" className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <span>
                                {t.identiteInviter}{" "}
                                <button type="button" onClick={() => setFeuille(true)} className={LIEN}>
                                    {t.identiteEnregistrer}
                                </button>
                            </span>
                        </p>
                    )}
                </div>
            )}

            {banniereReprise}
            <HauteurFluide>
                <div key={rempli || identiteRemplie || seance.n > 0 ? cle : "vierge"}>{children}</div>
            </HauteurFluide>

            {ligneActions}
            {carteSuite}

            <AnimatePresence>
                {changements.length > 0 && (
                    <Proposition
                        key="dossier"
                        titre={dossierVide(profil) ? t.creerTitre : t.garderTitre}
                        detail={dossierVide(profil) ? (connecte ? t.creerDetailCompte : t.creerDetail) : null}
                        changements={changements}
                        lang={lang}
                        onOui={garder}
                        onNon={() => setAnnonces((a) => ({ ...a, base: a.actuel }))}
                    />
                )}
                {changementsIdentite.length > 0 && (
                    <Proposition
                        key="identite"
                        titre={t.identiteGarderTitre}
                        detail={t.identiteGarderDetail}
                        changements={changementsIdentite}
                        lang={lang}
                        onOui={garderIdentite}
                        onNon={() => setAnnonces((a) => ({ ...a, baseId: a.actuelId }))}
                    />
                )}
                {confirme && <Confirmation key="confirme" texte={confirme} />}
            </AnimatePresence>
            {/* Une région toujours présente : un lecteur d'écran n'annonce
                sûrement que le texte qui change dans une région déjà là. */}
            <div aria-live="polite" className="sr-only">
                {confirme || annonce || ""}
            </div>

            {estSimulateur && (
                <BottomSheetModal
                    isOpen={reprise.ouverte}
                    onClose={() => setReprise({ ouverte: false, question: null })}
                    maxWidthClass="sm:max-w-2xl"
                    labelledBy="dossier-reprise-titre"
                >
                    {reprise.ouverte && (
                        <div className="p-6 sm:p-8">
                            {reprise.question ? (
                                <ToolsDossierQuestions
                                    lang={lang}
                                    depart={reprise.question}
                                    seule
                                    // La réponse corrigée est déjà dans le dossier : l'outil
                                    // repart avec elle, et la feuille se ferme.
                                    onTermine={() => {
                                        setReprise({ ouverte: false, question: null });
                                        setSeance((x) => ({ n: x.n + 1, vierge: false }));
                                    }}
                                    // Ouverte depuis une valeur du bandeau, « Annuler » referme ;
                                    // depuis la liste, il y ramène.
                                    onQuitter={() => setReprise(reprise.directe ? { ouverte: false, question: null } : { ouverte: true, question: null })}
                                />
                            ) : (
                                <>
                                    <h2 id="dossier-reprise-titre" className="mb-2 text-xl font-bold font-sourceSans dark:text-white">
                                        {t.feuilleTitre}
                                    </h2>
                                    <p className="mb-4 text-sm text-gray-600 dark:text-lightBlue">{t.feuilleIntro}</p>
                                    <ul className="divide-y divide-gray-200/70 dark:divide-white/10">
                                        {questionsReprises.map((q) => (
                                            <li key={q} className="flex items-center justify-between gap-4 py-2">
                                                <span className="min-w-0">
                                                    <span className="block text-xs text-gray-500 dark:text-lightBlue/70">{t.questions[q]}</span>
                                                    <span className="text-sm text-gray-900 dark:text-white">{resumeQuestion(q, profil, t, lang)}</span>
                                                </span>
                                                <button type="button" onClick={() => setReprise({ ouverte: true, question: q })} className={LIEN_SEUL}>
                                                    {t.modifier}
                                                    <span className="sr-only"> : {t.questions[q]}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    )}
                </BottomSheetModal>
            )}

            {estFormulaire && (
                <ToolsIdentitySheet
                    lang={lang}
                    ouvert={feuille}
                    onFermer={() => setFeuille(false)}
                    onEnregistre={() => setSeance((s) => ({ n: s.n + 1, vierge: false }))}
                />
            )}
        </ContexteDossier.Provider>
        </ContexteReprise.Provider>
    );
}
